// Map-trie keying primitive — single SSOT for "compound key in a JS
// Map", shared by every site that needs structural equality across a
// tuple of values (memoization caches, group-by / partition-by
// buckets, distinct / dedupe). The trie owns the per-level Map walk;
// callers own value-normalization (a `cellKey`, a `dateKey`, identity,
// or anything else) and what they put at the leaf.

/**
 * Sentinel slot for the arity-zero case. Callers don't write to this
 * key for non-empty key tuples, so there's no collision risk.
 */
const ROOT_SLOT = Symbol('map-trie/root');

/**
 * Walk a Map-trie to the slot identified by `keys`, returning the existing
 * leaf or creating one via `create()` on a miss (and storing it). One walk,
 * no intermediate get/set closure pair or `slice` allocation — `create` runs
 * only when the slot is empty, so callers can use its invocation as the
 * "first time I saw this key" signal (e.g. pushing a fresh bucket into an
 * ordered accumulator).
 *
 * Each key indexes one level of the trie via JS `Map` equality
 * (SameValueZero). Pre-normalize keys before calling when the desired
 * logical equality differs from SameValueZero (e.g. distinct `Date` objects
 * with the same instant collapse via `dateKey`; structurally-equal struct
 * cells collapse via `cellKey`).
 *
 * Empty `keys` resolves to a single root slot — used by `trieMemoize` for
 * arity-zero memoization.
 *
 * Note: a stored leaf of `undefined` is indistinguishable from an absent
 * slot, so `create` would re-run for such a leaf. Callers that may store
 * `undefined` wrap it (see `trieMemoize`'s `Leaf`).
 */
export const trieGetOrCreate = <LeafType>(
  root: Map<unknown, unknown>,
  keys: readonly unknown[],
  create: () => LeafType,
): LeafType => {
  const lastIdx = keys.length - 1;
  // Walk to the penultimate level, creating intermediate Maps lazily. No
  // `slice`: the final key indexes the leaf, not a trie level, so stop short.
  const node = keys.reduce<Map<unknown, unknown>>((n, k, i) => {
    if (i === lastIdx) {
      return n;
    }
    const next = n.get(k);
    if (next instanceof Map) {
      return next;
    }
    const fresh = new Map<unknown, unknown>();
    n.set(k, fresh);
    return fresh;
  }, root);
  const last = keys.length === 0 ? ROOT_SLOT : keys[lastIdx];
  const existing = node.get(last);
  if (existing !== undefined) {
    return existing as LeafType;
  }
  const created = create();
  node.set(last, created);
  return created;
};

/**
 * Marker wrapper for "leaf has been computed, here is the result".
 * Wrapping (rather than gating on `Map.has`) lets `fn` return
 * `undefined` and still cache once — and on the cache-hit path the
 * single `!== undefined` check is cheaper than a `has` + `get` pair.
 */
type Leaf<ResultType> = { readonly value: ResultType };

const identity = (v: unknown): unknown => v;

/**
 * Memoize a fixed-arity pure function via a `Map`-trie keyed on its
 * arguments. The returned function has the same signature as `fn`.
 *
 * `normalize` is applied to EACH argument before it indexes a level.
 * Default is identity — pass a custom normalizer when an argument's
 * Map-equality (SameValueZero) does not match the desired logical
 * equality (the canonical example: `Date`).
 *
 * Trade-offs:
 *   - Per-call cost: `args.length` Map lookups + one leaf get. No
 *     composite string key, no `JSON.stringify`, no `join`.
 *   - Memory: one inner `Map` per distinct prefix path. Unbounded by
 *     design — intended for either short-lived scratch caches (one
 *     verb call) or low-cardinality long-lived caches (a handful of
 *     distinct timezone / period combinations per session). Do NOT
 *     use for high-cardinality inputs without an external eviction
 *     strategy.
 *   - `WeakMap` is not applicable here: WeakMap requires object keys
 *     and our keys are primitives (or `Date` normalized to a number).
 *
 * Domain-agnostic on purpose — knows nothing about `Date`, timezones,
 * or any other value shape. Callers wire in their own normalizer when
 * they need value-equality over object keys.
 */
export const trieMemoize = <ArgsType extends readonly unknown[], ResultType>(
  fn: (...args: ArgsType) => ResultType,
  normalize: (v: unknown) => unknown = identity,
): ((...args: ArgsType) => ResultType) => {
  const root = new Map<unknown, unknown>();
  return (...args: ArgsType): ResultType =>
    trieGetOrCreate<Leaf<ResultType>>(root, args.map(normalize), () => ({ value: fn(...args) })).value;
};
